import { Component, OnInit, EventEmitter, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ActionButtonEvent } from 'src/app/layout/action-button-toolbar/action-button-toolbar.component';
import { ColumnSetting } from 'src/app/layout/table-standard-layout/table-standard-layout.component';
import { CompanyUserService } from '../company-user.service';
import { combineLatest } from 'rxjs';
import { CommonPopup } from 'src/app/admin-dashboard/common-popup/common-popup';
import {  MatDialog } from '@angular/material/dialog';
import { map } from 'rxjs/operators';
import { UpdateCompanyInUserPopup } from './update-company-in-user-popup/update-company-in-user-popup';
import { UpdateUserRolePopup } from './update-user-role-popup/update-user-role-popup';
import { UpdateSitesPopup } from './update-sites-popup/update-sites-popup';
import moment from  'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { AccessLevelsComponent } from './access-levels/access-levels.component';
import { DialogDelete } from 'src/app/dialog-delete/dialog-delete.component';
import { AuditReasonDialogComponent } from 'src/app/shared/audit-reason-dialog/audit-reason-dialog.component';

export interface Roles {
  admin: boolean;
  editor: boolean;
  subscriber: boolean;
  driver: boolean;
}

@Component({
  selector: 'app-company-users-list',
  templateUrl: './company-users-list.component.html',
  styleUrls: ['./company-users-list.component.css']
})
export class CompanyUsersListComponent implements OnInit, OnDestroy {

  combObs$ = new Observable()
  users$ = new Observable()

  companies$ = new Observable()
  rowActionButtons: { icon: string; tooltip: string; eventEmitter: EventEmitter<ActionButtonEvent>; }[];
  topbarActionButton: { icon: string; tooltip: string; eventEmitter: EventEmitter<ActionButtonEvent>; }[];

  updateCompany: EventEmitter<ActionButtonEvent>;
  onUpdateCompanySubscription: Subscription;

  deleteUser: EventEmitter<ActionButtonEvent>;
  onDeleteUserSubscription: Subscription;

  toggleDisabled: EventEmitter<ActionButtonEvent>;
  onToggleDisabledSubscription: Subscription;

  updateAccountType: EventEmitter<ActionButtonEvent>;
  onUpdateAccountTypeSubscription: Subscription;

  updateRoles: EventEmitter<ActionButtonEvent>;
  onUpdateRolesSubscription: Subscription;

  resetPassword: EventEmitter<ActionButtonEvent>;
  onResetPasswordSubscription: Subscription;

  updateSites: EventEmitter<ActionButtonEvent>;
  onUpdateSitesSubscription: Subscription;

  addUser: EventEmitter<ActionButtonEvent>;
  onAddUserSubscription: Subscription;

  addCompany: EventEmitter<ActionButtonEvent>;
  onAddCompanySubscription: Subscription;

  accessLevel: EventEmitter<ActionButtonEvent>;
  onAccessLevelSubscription: Subscription;

  siteId;
  systemId;

  // Firebase Auth's password policy on this project requires lower + upper +
  // digit + a non-alphanumeric character, min length 6. Generates a short
  // one that satisfies it so the operator doesn't have to guess a password
  // that passes, and can just read/copy it straight from the field.
  // Look-alike characters (0/O, 1/l/I) are excluded for readability.
  private generatePassword(length = 10): string {
    const sets = ['abcdefghjkmnpqrstuvwxyz', 'ABCDEFGHJKMNPQRSTUVWXYZ', '23456789', '!@#$%^&*'];
    const all = sets.join('');
    const pick = (chars: string) => chars[Math.floor(Math.random() * chars.length)];

    const passwordChars = sets.map(pick);
    while (passwordChars.length < length) passwordChars.push(pick(all));

    // Fisher-Yates shuffle so the required characters aren't always in the same position.
    for (let i = passwordChars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]];
    }
    return passwordChars.join('');
  }

  columnSettings: ColumnSetting[] = [
    {
      columnId: 'Name',
      name: 'Name'
    }, {
      columnId: 'email',
      name: 'Email',
      hideOnMobile: true
    }, 
    // {
    //   columnId: 'roles',
    //   name: 'Roles',
    //   hideOnMobile: true
    // }, 
    // {
    //   columnId: 'companyNames',
    //   name: 'Company',
    //   hideOnMobile: true
    // },
    {
      columnId: 'accountTypeLabel',
      name: 'Type',
      hideOnMobile: true
    },
    {
      columnId: 'statusLabel',
      name: 'Status',
      hideOnMobile: true
    },
    {
      columnId: 'signedIn',
      name: 'Signed In',
      hideOnMobile: true
    }]

  constructor(private companyUserService: CompanyUserService, private dialog: MatDialog) {

    this.companies$ = this.companyUserService.getCompanies$()

    this.combObs$ = combineLatest(this.companyUserService.getUsers$(), this.companies$).pipe(map(arr => {
      return arr[0].map(users => {

        return {
          ...users,
          accountTypeLabel: users['accountType'] === 'system' ? 'System' : 'User',
          statusLabel: users['disabled'] ? 'Disabled' : 'Active',
          signedIn: users['signedIn']  ? moment.unix(users['signedIn']).fromNow() : "No record",
          updatedOn: moment.unix(users['updatedOn']).format("YYYY-MM-DD HH:mm"),
          // .filter(Boolean) drops companyIds that no longer resolve to a live
          // company doc (e.g. the company was deleted) — otherwise this collapses
          // to `undefined` and crashes the Update Company / Update Sites popups.
          companies: Array.isArray(users['companyId']) ? users['companyId'].map(companyId =>
            (arr[1] as Array<any>).filter(company => company['companyId'] === companyId).map(companyName => {
              return {
                name: companyName['name'],
                companyId: companyName['companyId']
              }
            })[0]
          ).filter(Boolean) : [],
          companyNames: Array.isArray(users['companyId']) ? users['companyId'].map(companyId =>
            (arr[1] as Array<any>).filter(company => company['companyId'] === companyId).map(companyName => {
              return companyName['name']
            })[0]
          ).filter(Boolean) : []
        }
      })
    }))

    this.updateCompany = new EventEmitter<ActionButtonEvent>();
    this.updateRoles = new EventEmitter<ActionButtonEvent>();
    this.resetPassword = new EventEmitter<ActionButtonEvent>();
    this.updateSites = new EventEmitter<ActionButtonEvent>();
    this.addUser = new EventEmitter<ActionButtonEvent>();
    this.addCompany = new EventEmitter<ActionButtonEvent>();
    this.accessLevel = new EventEmitter<ActionButtonEvent>();
    this.deleteUser = new EventEmitter<ActionButtonEvent>();
    this.toggleDisabled = new EventEmitter<ActionButtonEvent>();
    this.updateAccountType = new EventEmitter<ActionButtonEvent>();

    this.rowActionButtons = [
      { icon: 'home', tooltip: 'Update Company', eventEmitter: this.updateCompany },
      { icon: 'business', tooltip: 'Update Sites', eventEmitter: this.updateSites },
      { icon: 'verified_user', tooltip: 'Update Role', eventEmitter: this.updateRoles },
      { icon: 'traffic', tooltip: 'Access', eventEmitter: this.accessLevel },
      { icon: 'autorenew', tooltip: 'Reset password', eventEmitter: this.resetPassword },
      { icon: 'swap_horiz', tooltip: 'Update Account Type', eventEmitter: this.updateAccountType },
      { icon: 'block', tooltip: 'Disable / Enable account', eventEmitter: this.toggleDisabled },
      // { icon: 'remove', tooltip: 'Remove user', eventEmitter: this.deleteUser },

    ]

    this.topbarActionButton = [
      { icon: 'person', tooltip: 'Add User', eventEmitter: this.addUser },
      { icon: 'home', tooltip: 'Add Company', eventEmitter: this.addCompany },

    ]


    this.onUpdateCompanySubscription = this.updateCompany.subscribe(event => {
      let data = event.eventData
      if (data) {
        const dialogRef = this.dialog.open(UpdateCompanyInUserPopup, {
          width: '550px',
          disableClose: true,
          data: data
        });
        // dialogRef.afterClosed().subscribe(result => {
        //   console.log('The dialog was closed');
        // });
      }
    });

    this.onDeleteUserSubscription = this.deleteUser.subscribe(event => {
      let data = event.eventData
      if (data) {
        const dialogRef = this.dialog.open(DialogDelete, {
          width: '550px',
          disableClose: true,
          data: {
            formData: {
              title: "DELETE",
              message: " Are you sure to continue? ",
              buttons: ['Cancel', 'Yes']
            }
          }
        });
        dialogRef.afterClosed().subscribe(result => {
          console.log('The dialog was closed');
          if(result){
            this.companyUserService.delete(data['uid'])
          }
        });
      }
    });


    this.onUpdateAccountTypeSubscription = this.updateAccountType.subscribe(event => {
      let data = event.eventData
      if (!data) return;

      const dialogRef = this.dialog.open(CommonPopup, {
        disableClose: true,
        width: '500px',
        data: {
          formData: {
            title: "Update Account Type",
            inputFeilds: [
              {
                labelName: "Account Type",
                name: "accountType",
                required: true,
                value: data['accountType'] === 'system' ? 'system' : 'user',
                selectOptions: [
                  { value: "user", label: "User" },
                  { value: "system", label: "System" }
                ],
                specialText: "System accounts are exempt from the 6-month inactivity auto-deactivation."
              }
            ],
            buttons: [
              {
                name: "UPDATE",
                type: "submit",
                color: "primary",
                validate: true
              }
            ]
          }
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.companyUserService.updateUser(data['uid'], { accountType: result.value.accountType });
        }
      });
    });

    this.onToggleDisabledSubscription = this.toggleDisabled.subscribe(event => {
      let data = event.eventData
      if (!data) return;

      const isCurrentlyDisabled = !!data['disabled'];

      if (isCurrentlyDisabled) {
        // Re-enabling: simple confirm, no reason needed.
        const dialogRef = this.dialog.open(DialogDelete, {
          width: '550px',
          disableClose: true,
          data: {
            formData: {
              title: "ENABLE ACCOUNT",
              message: `Re-enable login for ${data['email']}?`,
              buttons: ['Cancel', 'Yes']
            }
          }
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.companyUserService.setAccountDisabled(data['uid'], false);
          }
        });
      } else {
        // Disabling: ask for a reason, same pattern as the audit-logged delete flow.
        const dialogRef = this.dialog.open(AuditReasonDialogComponent, {
          width: '440px',
          disableClose: true,
          data: {
            title: 'Disable Account',
            entityName: data['email'],
            confirmLabel: 'Disable',
            danger: true
          }
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.companyUserService.setAccountDisabled(data['uid'], true, result.reason);
          }
        });
      }
    });

    this.onResetPasswordSubscription = this.resetPassword.subscribe(event => {
      let data = event.eventData
      if (data) {
        const dialogRef = this.dialog.open(CommonPopup, {
          disableClose: true,
          width: '500px',
          data: {
            text: '',
            formData: {
              title: `Reser Password`,
              inputFeilds: [
                {
                  labelName: "Email",
                  name: "email",
                  required: true,
                  type: "text",
                  value: data['email'],
                  specialText: ""
                }
              ],
              buttons: [
                {
                  name: "Send",
                  type: "submit",
                  color: "primary",
                  validate: false
                }
              ]
            }
          }
        });

        dialogRef.afterClosed().subscribe(async result => {
          if (result) {
            this.companyUserService.resetPassword(result.value.email)
          }
        });
      }
    });

    this.onUpdateRolesSubscription = this.updateRoles.subscribe(event => {
      let data = event.eventData
      if (data) {
        const dialogRef = this.dialog.open(UpdateUserRolePopup, {
          width: '550px',
          disableClose: true,
          data: data
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            let newRolesList: Roles = {
              admin: false,
              editor: false,
              subscriber: false,
              driver: false
            }
            result.forEach(element => {
              newRolesList[element] = true
            });

            this.companyUserService.updateRoles(data['uid'], newRolesList)

          }
        });
      }
    });


    this.onAccessLevelSubscription = this.accessLevel.subscribe(event => {
      let data = event.eventData
      if (data) {
        const dialogRef = this.dialog.open(AccessLevelsComponent, {
          width: '550px',
          disableClose: true,
          data: data
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) { 
            let newAccessList = {
              overview: false,
              products: false,
              setting: false,
              category: false,
              routines: false,
              lastConnection:false,
              orders: false,
              refund:false,
              productRegulering : false,
              companyReportPermission : false,
              campaignPermission: false,
              salesReport: false,
              support: false,
              shell: false,
              stocking: false,
              extraDeliveries: false,
              systemConfigs: false,
              actions: false

            }
            result.forEach(element => {
              newAccessList[element] = true
            });

            this.companyUserService.updateUser(data['uid'], newAccessList);
          }
        });
      }
    });


    this.onUpdateSitesSubscription = this.updateSites.subscribe(event => {
      let data = event.eventData
      if (data) {
        const dialogRef = this.dialog.open(UpdateSitesPopup, {
          width: '550px',
          disableClose: true,
          data: data
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.companyUserService.updateUserSites(data['uid'], result);
          }
        });
      }
    });


    this.onAddCompanySubscription = this.addCompany.subscribe(event => {
      const dialogRef = this.dialog.open(CommonPopup, {
        disableClose: true,
        width: '500px',
        data: {
          formData: {
            title: "Add Company",
            inputFeilds: [
              {
                labelName: "Name",
                name: "name",
                required: true,
                type: "text",
                specialText: ""
              },
              {
                labelName: "Detail",
                name: "detail",
                required: true,
                type: "text",
                specialText: ""
              }
            ],
            buttons: [
              {
                name: "ADD",
                type: "submit",
                color: "primary",
                validate: true
              }
            ]
          }
        }
      });
  
      dialogRef.afterClosed().subscribe(result => {
  
        if (result) {
          this.companyUserService.addCompany(result.value)
        }
      });
    });

    this.onAddUserSubscription = this.addUser.subscribe(event => {

      const dialogRef = this.dialog.open(CommonPopup, {
        disableClose: true,
        width: '500px',
        data: {
          formData: {
            title: "Add User",
            inputFeilds: [
              {
                labelName: "Email",
                name: "email",
                required: true,
                type: "text",
                specialText: ""
              },
              {
                labelName: "Password",
                name: "password",
                required: true,
                type: "text",
                value: this.generatePassword(),
                specialText: "Auto-generated for testing — they'll also get an email to set their own password"
              },
              {
                labelName: "Name",
                name: "Name",
                required: true,
                type: "text",
                specialText: ""
              },
              {
                labelName: "Account Type",
                name: "accountType",
                required: true,
                value: "user",
                selectOptions: [
                  { value: "user", label: "User" },
                  { value: "system", label: "System" }
                ],
                specialText: "System accounts are exempt from the 6-month inactivity auto-deactivation."
              }
            ],
            buttons: [
              {
                name: "ADD",
                type: "submit",
                color: "primary",
                validate: true
              }
            ],
            // Keeps the dialog open (with what was typed) and shows the reason
            // inline when Firebase Auth rejects the input, e.g. a password
            // that doesn't meet its policy — instead of closing regardless of
            // outcome and losing the form.
            onSubmit: (value) => this.companyUserService.addUser(value)
          }
        }
      });
    });

  }

  ngOnInit() {
  }


  ngOnDestroy(){
    this.onAddCompanySubscription.unsubscribe()
    this.onUpdateRolesSubscription.unsubscribe()
    this.onResetPasswordSubscription.unsubscribe()
    this.onUpdateSitesSubscription.unsubscribe()
    this.onAddUserSubscription.unsubscribe()
    this.onUpdateCompanySubscription.unsubscribe()
    this.onToggleDisabledSubscription.unsubscribe()
    this.onUpdateAccountTypeSubscription.unsubscribe()
  }
}
